import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { Plus, Search, Eye, Trash2, Mic, MicOff, Loader2, AlertCircle, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePagamentiPendenti } from '../contexts/PagamentiPendentiContext';
import { useVoiceRecognition } from '../hooks/useVoiceRecognition';
import SmartFilters from '../components/SmartFilters';
import { parseApiError } from '../lib/parseApiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TICKET_FILTER_LABELS = { stato: 'Stato', tecnico_assegnato_id: 'Tecnico' };

const PRIORITA = ['bassa', 'media', 'alta', 'urgente'];
const STATI = ['aperto', 'in_lavorazione', 'chiuso', 'annullato'];

export default function TicketsPage() {
  const { isAdmin, user } = useAuth();
  const { hasPagamentoPendente, getPagamentoPendenteInfo } = usePagamentiPendenti();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [tecnici, setTecnici] = useState([]);
  const [puntiVendita, setPuntiVendita] = useState([]);
  const [impianti, setImpianti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroStato, setFiltroStato] = useState('');
  const [filtroTecnico, setFiltroTecnico] = useState('');
  const currentFilters = useMemo(() => ({ stato: filtroStato, tecnico_assegnato_id: filtroTecnico }), [filtroStato, filtroTecnico]);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Search cliente
  const [clienteSearch, setClienteSearch] = useState('');
  const [clienteSearchResults, setClienteSearchResults] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);
  
  const [formData, setFormData] = useState({
    cliente_id: '',
    punto_vendita_id: '',
    impianto_id: '',
    oggetto: '',
    descrizione: '',
    priorita: 'media',
    tecnico_assegnato_id: ''
  });
  
  // Voice input states
  const [voiceDialogOpen, setVoiceDialogOpen] = useState(false);
  const [voiceParsing, setVoiceParsing] = useState(false);
  const [voiceParsedData, setVoiceParsedData] = useState(null);
  
  // Use centralized voice recognition hook
  const {
    isListening,
    isSupported: voiceSupported,
    transcript: voiceText,
    startListening,
    stopListening,
    resetTranscript,
    setTranscript: setVoiceText
  } = useVoiceRecognition({ lang: 'it-IT', continuous: true, showToasts: false });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ticketsRes, clientiRes, tecniciRes, impiantiRes] = await Promise.all([
        axios.get(`${API}/tickets`),
        axios.get(`${API}/clienti`),
        axios.get(`${API}/tecnici`),
        axios.get(`${API}/impianti`)
      ]);
      setTickets(ticketsRes.data);
      setClienti(clientiRes.data);
      setTecnici(tecniciRes.data);
      setImpianti(impiantiRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // Search cliente by name or P.IVA
  const searchClienti = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setClienteSearchResults([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/clienti/search?q=${encodeURIComponent(query)}`);
      setClienteSearchResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchClienti(clienteSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [clienteSearch, searchClienti]);

  // Load punti vendita when cliente changes (impianti already loaded globally)
  const handleSelectCliente = async (cliente) => {
    setSelectedCliente(cliente);
    setClienteSearch(cliente.ragione_sociale);
    setClienteSearchResults([]);
    setFormData({ ...formData, cliente_id: cliente.id, punto_vendita_id: '', impianto_id: '' });
    
    try {
      const puntiRes = await axios.get(`${API}/punti-vendita?cliente_id=${cliente.id}`);
      setPuntiVendita(puntiRes.data);
    } catch (error) {
      console.error('Error loading punti vendita:', error);
    }
  };

  // Get impianti for selected cliente
  const getClienteImpianti = () => {
    if (!selectedCliente) return [];
    return impianti.filter(i => i.cliente_id === selectedCliente.id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };
      if (!data.impianto_id) delete data.impianto_id;
      if (!data.punto_vendita_id) delete data.punto_vendita_id;
      if (!data.tecnico_assegnato_id) delete data.tecnico_assegnato_id;
      
      await axios.post(`${API}/tickets`, data);
      toast.success('Ticket creato');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore nel salvataggio'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo ticket?')) return;
    try {
      await axios.delete(`${API}/tickets/${id}`);
      toast.success('Ticket eliminato');
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore eliminazione'));
    }
  };

  const resetForm = () => {
    setFormData({
      cliente_id: '',
      punto_vendita_id: '',
      impianto_id: '',
      oggetto: '',
      descrizione: '',
      priorita: 'media',
      tecnico_assegnato_id: ''
    });
    setSelectedCliente(null);
    setClienteSearch('');
    setClienteSearchResults([]);
    setPuntiVendita([]);
  };

  const getClienteName = (clienteId) => {
    const cliente = clienti.find(c => c.id === clienteId);
    return cliente?.ragione_sociale || '-';
  };

  const getTecnicoName = (tecnicoId) => {
    const tecnico = tecnici.find(t => t.id === tecnicoId);
    return tecnico ? `${tecnico.nome} ${tecnico.cognome}` : '-';
  };

  const getImpiantoInfo = (impiantoId) => {
    const imp = impianti.find(i => i.id === impiantoId);
    if (!imp) return null;
    return {
      tipo: imp.tipo,
      marca: imp.marca,
      modello: imp.modello,
      matricola: imp.matricola
    };
  };

  const filteredTickets = tickets.filter(t => {
    const matchSearch = 
      t.numero?.toString().includes(search) ||
      t.oggetto?.toLowerCase().includes(search.toLowerCase()) ||
      getClienteName(t.cliente_id)?.toLowerCase().includes(search.toLowerCase());
    const matchStato = !filtroStato || t.stato === filtroStato;
    const matchTecnico = !filtroTecnico || t.tecnico_assegnato_id === filtroTecnico;
    return matchSearch && matchStato && matchTecnico;
  });

  // ===== VOICE INPUT FUNCTIONS =====
  
  const openVoiceDialog = () => {
    if (!voiceSupported) {
      toast.error('Il tuo browser non supporta il riconoscimento vocale. Usa Chrome o Edge.');
      return;
    }
    
    setVoiceParsedData(null);
    resetTranscript();
    setVoiceDialogOpen(true);
    startListening();
    toast.info('Parla ora... Es: "Ticket per Mario, registratore bloccato, priorità alta"');
  };
  
  const handleStopVoiceInput = () => {
    stopListening();
    
    if (voiceText.trim()) {
      parseVoiceTicket(voiceText);
    }
  };
  
  const restartVoiceListening = () => {
    resetTranscript();
    setVoiceParsedData(null);
    startListening();
  };
  
  const cancelVoiceInput = () => {
    stopListening();
    setVoiceDialogOpen(false);
    resetTranscript();
    setVoiceParsedData(null);
  };
  
  const parseVoiceTicket = (text) => {
    setVoiceParsing(true);
    const textLower = text.toLowerCase();
    
    // Trova cliente menzionato
    let clienteTrovato = null;
    for (const c of clienti) {
      const ragioneSociale = c.ragione_sociale.toLowerCase();
      const parole = ragioneSociale.split(' ').filter(p => p.length > 3);
      for (const parola of parole) {
        if (textLower.includes(parola)) {
          clienteTrovato = c;
          break;
        }
      }
      if (clienteTrovato) break;
    }
    
    // Trova tecnico menzionato
    let tecnicoTrovato = null;
    for (const t of tecnici) {
      const nomeCompleto = `${t.nome} ${t.cognome}`.toLowerCase();
      const soloNome = t.nome.toLowerCase();
      if (textLower.includes(nomeCompleto) || textLower.includes(soloNome)) {
        tecnicoTrovato = t;
        break;
      }
    }
    
    // Estrai priorità
    let priorita = 'media';
    if (textLower.includes('urgente') || textLower.includes('urgenza')) {
      priorita = 'urgente';
    } else if (textLower.includes('alta') || textLower.includes('importante')) {
      priorita = 'alta';
    } else if (textLower.includes('bassa')) {
      priorita = 'bassa';
    }
    
    // Estrai oggetto - cerca pattern comuni
    let oggetto = '';
    // Parole chiave comuni per problemi
    const keywords = ['bloccato', 'rotto', 'non funziona', 'errore', 'guasto', 'problema', 
                      'assistenza', 'manutenzione', 'verifica', 'controllo', 'installazione',
                      'sostituzione', 'riparazione', 'aggiornamento'];
    
    for (const kw of keywords) {
      if (textLower.includes(kw)) {
        // Cerca il contesto intorno alla keyword
        const idx = textLower.indexOf(kw);
        const start = Math.max(0, idx - 30);
        const end = Math.min(text.length, idx + kw.length + 30);
        oggetto = text.substring(start, end).trim();
        break;
      }
    }
    
    // Se non trovato oggetto specifico, usa tutto il testo (senza il nome cliente)
    if (!oggetto) {
      oggetto = text;
      if (clienteTrovato) {
        // Rimuovi il nome del cliente dal testo
        const clienteWords = clienteTrovato.ragione_sociale.split(' ');
        for (const word of clienteWords) {
          if (word.length > 3) {
            oggetto = oggetto.replace(new RegExp(word, 'gi'), '').trim();
          }
        }
      }
    }
    
    // Pulisci l'oggetto
    oggetto = oggetto
      .replace(/ticket\s*(per)?\s*/gi, '')
      .replace(/priorit[aà]\s*(alta|bassa|media|urgente)/gi, '')
      .replace(/assegna(re)?\s*a\s*/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
    
    // Limita lunghezza oggetto
    if (oggetto.length > 100) {
      oggetto = oggetto.substring(0, 100) + '...';
    }
    
    setVoiceParsedData({
      testoOriginale: text,
      oggetto: oggetto || 'Nuovo ticket',
      descrizione: text,
      cliente: clienteTrovato,
      tecnico: tecnicoTrovato,
      priorita: priorita
    });
    
    setVoiceParsing(false);
  };
  
  const confirmVoiceTicket = async () => {
    if (!voiceParsedData) return;
    
    // Imposta i dati nel form
    if (voiceParsedData.cliente) {
      await handleSelectCliente(voiceParsedData.cliente);
    }
    
    setFormData(prev => ({
      ...prev,
      oggetto: voiceParsedData.oggetto,
      descrizione: voiceParsedData.descrizione,
      priorita: voiceParsedData.priorita,
      tecnico_assegnato_id: voiceParsedData.tecnico?.id || ''
    }));
    
    // Chiudi dialog voce e apri form
    setVoiceDialogOpen(false);
    setVoiceParsedData(null);
    setVoiceText('');
    setDialogOpen(true);
  };
  
  // ===== END VOICE INPUT FUNCTIONS =====

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground font-mono text-sm">CARICAMENTO...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tickets-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Tickets</h1>
          <p className="text-muted-foreground text-sm mt-1">{tickets.length} tickets totali</p>
        </div>
        
        <div className="flex gap-2">
          {/* Bottone Voce */}
          <Button 
            onClick={openVoiceDialog} 
            variant="outline"
            className="rounded-none border-cyan-500 text-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-950"
            data-testid="voice-ticket-btn"
          >
            <Mic className="h-4 w-4 mr-2" />
            Voce
          </Button>
          
          {/* Dialog Nuovo Ticket */}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
              <Button className="rounded-none font-mono uppercase tracking-wider" data-testid="new-ticket-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nuovo Ticket
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-xl rounded-none">
              <DialogHeader>
                <DialogTitle className="font-mono uppercase tracking-wider">Nuovo Ticket</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-4">
                  {/* Cliente Search */}
                  <div className="relative">
                    <Label className="text-xs font-mono uppercase">Cliente * (cerca per nome o P.IVA)</Label>
                    <Input
                      value={clienteSearch}
                      onChange={(e) => {
                        setClienteSearch(e.target.value);
                        if (selectedCliente) {
                          setSelectedCliente(null);
                          setFormData({ ...formData, cliente_id: '', punto_vendita_id: '', impianto_id: '' });
                          setPuntiVendita([]);
                          setImpianti([]);
                        }
                      }}
                      placeholder="Digita e seleziona dalla lista..."
                      className={`rounded-none ${selectedCliente ? 'border-success bg-success/5' : ''}`}
                      data-testid="ticket-cliente-search"
                    />
                    {clienteSearchResults.length > 0 && !selectedCliente && (
                      <div className="absolute z-50 w-full mt-1 bg-background border-2 border-primary shadow-lg max-h-48 overflow-y-auto">
                        <div className="p-2 bg-muted text-xs font-mono uppercase text-muted-foreground">
                          Clicca per selezionare:
                        </div>
                        {clienteSearchResults.map((c) => (
                          <div
                            key={c.id}
                            className="p-3 hover:bg-primary hover:text-primary-foreground cursor-pointer border-b border-border last:border-b-0 transition-colors"
                            onClick={() => handleSelectCliente(c)}
                          >
                            <p className="font-bold">{c.ragione_sociale}</p>
                            <p className="text-xs opacity-70">P.IVA: {c.partita_iva || '-'} | {c.citta_legale || ''}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {selectedCliente ? (
                      <div className="mt-2 p-2 bg-success/10 border border-success rounded-none">
                        <p className="text-sm font-bold text-success">✓ Cliente selezionato:</p>
                        <p className="text-sm font-medium">{selectedCliente.ragione_sociale}</p>
                        <p className="text-xs text-muted-foreground">P.IVA: {selectedCliente.partita_iva || '-'}</p>
                      </div>
                    ) : clienteSearch.length >= 2 && clienteSearchResults.length === 0 && (
                      <p className="text-xs text-destructive mt-1">Nessun cliente trovato. Verifica la ricerca.</p>
                    )}
                  </div>

                  {/* Avviso Pagamento Pendente */}
                  {selectedCliente && hasPagamentoPendente(selectedCliente.id) && (
                    <Alert variant="destructive" className="rounded-none border-2 border-destructive animate-pulse">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription className="text-sm">
                        <strong>ATTENZIONE!</strong> Questo cliente ha fatture/abbonamenti NON PAGATI.
                        {(() => {
                          const info = getPagamentoPendenteInfo(selectedCliente.id);
                          if (info) {
                            return (
                              <span className="block mt-1 font-mono text-xs">
                                {info.fatture_non_pagate ?? info.abbonamenti_non_pagati} fattura/e - Totale: € {info.totale_dovuto?.toFixed(2)}
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Punto Vendita */}
                  {selectedCliente && puntiVendita.length > 0 && (
                    <div>
                      <Label className="text-xs font-mono uppercase">Punto Vendita (dove mandare il tecnico)</Label>
                      <Select 
                        value={formData.punto_vendita_id || "none"} 
                        onValueChange={(value) => setFormData({...formData, punto_vendita_id: value === "none" ? "" : value, impianto_id: ''})}
                      >
                        <SelectTrigger className="rounded-none" data-testid="ticket-punto-vendita">
                          <SelectValue placeholder="Seleziona punto vendita" />
                        </SelectTrigger>
                        <SelectContent className="rounded-none">
                          <SelectItem value="none">Non specificato</SelectItem>
                          {puntiVendita.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.nome} - {p.indirizzo}, {p.citta}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Impianto */}
                  {selectedCliente && getClienteImpianti().length > 0 && (
                    <div>
                      <Label className="text-xs font-mono uppercase">Impianto (Tipo / Modello / Matricola)</Label>
                      <Select 
                        value={formData.impianto_id || "none"} 
                        onValueChange={(value) => setFormData({...formData, impianto_id: value === "none" ? "" : value})}
                      >
                        <SelectTrigger className="rounded-none" data-testid="ticket-impianto">
                          <SelectValue placeholder="Seleziona impianto (opzionale)" />
                        </SelectTrigger>
                        <SelectContent className="rounded-none">
                          <SelectItem value="none">Non specificato</SelectItem>
                          {(formData.punto_vendita_id 
                            ? getClienteImpianti().filter(i => i.punto_vendita_id === formData.punto_vendita_id) 
                            : getClienteImpianti()
                          ).map((i) => (
                            <SelectItem key={i.id} value={i.id}>
                              {i.tipo} {i.marca && `- ${i.marca}`} {i.modello && i.modello} | Matr: {i.matricola}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <Label className="text-xs font-mono uppercase">Oggetto *</Label>
                    <Input
                      value={formData.oggetto}
                      onChange={(e) => setFormData({...formData, oggetto: e.target.value})}
                      className="rounded-none"
                      required
                      data-testid="ticket-oggetto"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase">Descrizione *</Label>
                    <Textarea
                      value={formData.descrizione}
                      onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                      className="rounded-none min-h-24"
                      required
                      data-testid="ticket-descrizione"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-mono uppercase">Priorità</Label>
                      <Select 
                        value={formData.priorita} 
                        onValueChange={(value) => setFormData({...formData, priorita: value})}
                      >
                        <SelectTrigger className="rounded-none" data-testid="ticket-priorita">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-none">
                          {PRIORITA.map((p) => (
                            <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase">Assegna a</Label>
                      <Select 
                        value={formData.tecnico_assegnato_id || "none"} 
                        onValueChange={(value) => setFormData({...formData, tecnico_assegnato_id: value === "none" ? "" : value})}
                      >
                        <SelectTrigger className="rounded-none" data-testid="ticket-tecnico">
                          <SelectValue placeholder="Non assegnato" />
                        </SelectTrigger>
                        <SelectContent className="rounded-none">
                          <SelectItem value="none">Non assegnato</SelectItem>
                          {tecnici.map((t) => (
                            <SelectItem key={t.id} value={t.id}>{t.nome} {t.cognome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">
                    Annulla
                  </Button>
                  <Button type="submit" className="rounded-none font-mono uppercase" disabled={!selectedCliente} data-testid="save-ticket-btn">
                    Crea Ticket
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Dialog Input Vocale */}
      <Dialog open={voiceDialogOpen} onOpenChange={(open) => !open && cancelVoiceInput()}>
        <DialogContent className="max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle className="font-black flex items-center gap-2">
              <Mic className="h-5 w-5 text-cyan-500" />
              Ticket Vocale
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Stato ascolto animato */}
            {isListening && (
              <div className="text-center py-6">
                <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-cyan-500 flex items-center justify-center animate-pulse">
                  <Mic className="h-8 w-8 text-white" />
                </div>
                <p className="text-lg font-medium text-cyan-600">Sto ascoltando...</p>
              </div>
            )}

            {/* Area testo MODIFICABILE */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-mono uppercase">Testo (modifica se necessario):</Label>
                <span className="text-xs text-muted-foreground">
                  {voiceText.length} caratteri
                </span>
              </div>
              <Textarea
                value={voiceText}
                onChange={(e) => {
                  setVoiceText(e.target.value);
                  setVoiceParsedData(null);
                }}
                placeholder="Parla o scrivi qui... Es: 'Ticket per Mario, registratore bloccato, priorità alta'"
                className="rounded-none min-h-[100px] text-base"
                data-testid="voice-ticket-text-input"
              />
              
              <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
                <strong>Suggerimenti:</strong> Includi nome cliente, problema (bloccato, guasto, errore), priorità (alta/bassa/urgente), nome tecnico
              </div>
            </div>

            {/* Pulsanti azione */}
            <div className="flex gap-2">
              {isListening ? (
                <Button 
                  onClick={handleStopVoiceInput} 
                  className="flex-1 h-12 rounded-none bg-red-500 hover:bg-red-600"
                >
                  <MicOff className="h-5 w-5 mr-2" />
                  Ferma Registrazione
                </Button>
              ) : (
                <Button 
                  onClick={restartVoiceListening} 
                  variant="outline"
                  className="flex-1 h-12 rounded-none border-cyan-500 text-cyan-600"
                >
                  <Mic className="h-5 w-5 mr-2" />
                  {voiceText ? 'Aggiungi con Voce' : 'Inizia Registrazione'}
                </Button>
              )}
              
              {voiceText && !isListening && (
                <Button 
                  onClick={() => parseVoiceTicket(voiceText)} 
                  className="flex-1 h-12 rounded-none"
                  disabled={voiceParsing || !voiceText.trim()}
                >
                  {voiceParsing ? (
                    <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Analizzo...</>
                  ) : (
                    'Analizza Testo'
                  )}
                </Button>
              )}
            </div>

            {/* Risultato parsing */}
            {voiceParsedData && (
              <div className="space-y-4">
                <div className="p-4 border-2 border-cyan-500 bg-cyan-50 dark:bg-cyan-950 rounded">
                  <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                    ✅ Ticket Riconosciuto:
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Oggetto:</span>
                      <p className="font-medium text-lg">{voiceParsedData.oggetto}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Cliente:</span>
                      {voiceParsedData.cliente ? (
                        <p className="font-medium text-lg">{voiceParsedData.cliente.ragione_sociale}</p>
                      ) : (
                        <p className="text-amber-600">⚠️ Non trovato - cerca nel form</p>
                      )}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Priorità:</span>
                      <p className={`font-medium text-lg uppercase priority-${voiceParsedData.priorita}`}>
                        {voiceParsedData.priorita}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Tecnico:</span>
                      {voiceParsedData.tecnico ? (
                        <p className="font-medium">{voiceParsedData.tecnico.nome} {voiceParsedData.tecnico.cognome}</p>
                      ) : (
                        <p className="text-muted-foreground">Non assegnato</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setVoiceParsedData(null)} 
                    className="flex-1 rounded-none"
                  >
                    Modifica Testo
                  </Button>
                  <Button 
                    onClick={confirmVoiceTicket} 
                    className="flex-1 rounded-none bg-cyan-500 hover:bg-cyan-600"
                  >
                    Conferma → Apri Form
                  </Button>
                </div>
              </div>
            )}

            {/* Pulsante Annulla */}
            <Button 
              variant="ghost" 
              onClick={cancelVoiceInput} 
              className="w-full rounded-none text-muted-foreground"
            >
              Annulla
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Filters - Mobile Optimized */}
      <div className="flex flex-col gap-3">
        {/* Search row */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cerca ticket..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 rounded-md h-10"
              data-testid="search-tickets"
            />
            {search && (
              <button 
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          
          {/* Smart Filters */}
          <SmartFilters
            pageKey="tickets"
            currentFilters={currentFilters}
            onApplyFilter={(filters) => {
              setFiltroStato(filters.stato || '');
              setFiltroTecnico(filters.tecnico_assegnato_id || '');
            }}
            filterLabels={TICKET_FILTER_LABELS}
          />
        </div>

        {/* Filter dropdowns row */}
        <div className="flex flex-wrap gap-2">
          <Select value={filtroStato || "all"} onValueChange={(v) => setFiltroStato(v === "all" ? "" : v)}>
            <SelectTrigger className="w-full sm:w-40 rounded-md h-9 text-sm" data-testid="filter-stato">
              <SelectValue placeholder="Stato" />
            </SelectTrigger>
            <SelectContent className="rounded-md">
              <SelectItem value="all">Tutti gli stati</SelectItem>
              {STATI.map((s) => (
                <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filtroTecnico || "all"} onValueChange={(v) => setFiltroTecnico(v === "all" ? "" : v)}>
            <SelectTrigger className="w-full sm:w-44 rounded-md h-9 text-sm" data-testid="filter-tecnico">
              <SelectValue placeholder="Tecnico" />
            </SelectTrigger>
            <SelectContent className="rounded-md">
              <SelectItem value="all">Tutti i tecnici</SelectItem>
              {tecnici.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.nome} {t.cognome}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Active filters indicator */}
          {(filtroStato || filtroTecnico) && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setFiltroStato(''); setFiltroTecnico(''); }}
              className="h-9 text-xs text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3 mr-1" />
              Reset filtri
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="border border-border overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th className="bg-primary/10">#</th>
              <th className="bg-blue-600/10">Oggetto</th>
              <th className="hidden md:table-cell bg-cyan-600/10">Cliente</th>
              <th className="hidden lg:table-cell bg-teal-600/10">Impianto</th>
              <th className="bg-amber-600/10">Priorità</th>
              <th className="bg-purple-600/10">Stato</th>
              <th className="hidden xl:table-cell bg-emerald-600/10">Tecnico</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filteredTickets.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center text-muted-foreground py-8">
                  Nessun ticket trovato
                </td>
              </tr>
            ) : (
              filteredTickets.map((ticket) => (
                <tr 
                  key={ticket.id} 
                  className="hover:bg-muted transition-colors cursor-pointer"
                  onClick={() => navigate(`/tickets/${ticket.id}`)}
                >
                  <td className="font-mono font-bold bg-primary/5 border-l-4 border-l-primary">{ticket.numero}</td>
                  <td className="bg-blue-600/5 border-l-4 border-l-blue-600">
                    <span className="font-medium text-blue-600 dark:text-blue-400">
                      {ticket.oggetto}
                    </span>
                  </td>
                  <td className="hidden md:table-cell bg-cyan-600/5 text-cyan-600 dark:text-cyan-400 border-l-4 border-l-cyan-600">{getClienteName(ticket.cliente_id)}</td>
                  <td className="hidden lg:table-cell bg-teal-600/5 border-l-4 border-l-teal-600">
                    {ticket.impianto_id ? (
                      <div className="text-xs">
                        {(() => {
                          const imp = getImpiantoInfo(ticket.impianto_id);
                          if (!imp) return '-';
                          return (
                            <>
                              <span className="font-medium text-teal-600 dark:text-teal-400">{imp.tipo}</span>
                              {imp.marca && <span className="text-muted-foreground"> {imp.marca}</span>}
                              {imp.modello && <span className="text-muted-foreground"> {imp.modello}</span>}
                              <br />
                              <span className="font-mono text-muted-foreground">M: {imp.matricola}</span>
                            </>
                          );
                        })()}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="bg-amber-600/5 border-l-4 border-l-amber-600">
                    <span className={`priority-${ticket.priorita} uppercase text-xs font-mono`}>
                      {ticket.priorita}
                    </span>
                  </td>
                  <td className="bg-purple-600/5 border-l-4 border-l-purple-600">
                    <Badge className={`badge-status badge-${ticket.priorita === 'alta' || ticket.priorita === 'urgente' ? 'warning' : ticket.stato} rounded-none`}>
                      {ticket.stato?.replace('_', ' ')}
                    </Badge>
                  </td>
                  <td className="hidden xl:table-cell bg-emerald-600/5 text-emerald-600 dark:text-emerald-400 border-l-4 border-l-emerald-600">{getTecnicoName(ticket.tecnico_assegnato_id)}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <Link to={`/tickets/${ticket.id}`}>
                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none" data-testid={`view-ticket-${ticket.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </Link>
                      {isAdmin() && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none text-destructive" onClick={() => handleDelete(ticket.id)} data-testid={`delete-ticket-${ticket.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
